import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getPayroll, getEmployees, getCompanySettings, getDepartments, getDesignations, downloadForm16, bulkDownloadPayslips, getPayrollAvailableFilters, downloadPayslip, downloadSelectedPayslips } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Receipt, Download, Building2, User as UserIcon, Search, FileCheck, FileText, Archive, Briefcase, Loader2, Users, X } from 'lucide-react';
import { toast } from 'sonner';

const Payslips = ({ user, setUser }) => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [companyInfo, setCompanyInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  
  // Search and Form 16 states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployeeForForm16, setSelectedEmployeeForForm16] = useState('');
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('');
  const [form16DialogOpen, setForm16DialogOpen] = useState(false);
  const [downloadingForm16, setDownloadingForm16] = useState(false);
  
  // Bulk download states
  const [bulkDownloadDialogOpen, setBulkDownloadDialogOpen] = useState(false);
  const [bulkDesignation, setBulkDesignation] = useState('');
  const [bulkDepartment, setBulkDepartment] = useState('');
  const [bulkMonth, setBulkMonth] = useState('');
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear().toString());
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  
  // Available filters for bulk download (designations/departments with payslips)
  const [availableDesignations, setAvailableDesignations] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  
  // Selected payslips for multi-select download
  const [selectedPayslips, setSelectedPayslips] = useState([]);
  const [downloadingSelected, setDownloadingSelected] = useState(false);
  
  const currentYear = new Date().getFullYear();
  const financialYears = Array.from({ length: 5 }, (_, i) => {
    const year = currentYear - i;
    return `${year}-${(year + 1).toString().slice(-2)}`;
  });

  useEffect(() => {
    fetchData();
  }, []);
  
  // Fetch available filters when month/year/department changes
  useEffect(() => {
    if (bulkMonth && bulkYear) {
      fetchAvailableFilters();
    }
  }, [bulkMonth, bulkYear, bulkDepartment]);
  
  const fetchAvailableFilters = async () => {
    try {
      setLoadingFilters(true);
      const response = await getPayrollAvailableFilters(
        bulkMonth, 
        parseInt(bulkYear), 
        bulkDepartment || null
      );
      console.log('Available filters response:', response.data);
      console.log('Debug info:', response.data.debug_info);
      setAvailableDesignations(response.data.designations || []);
      setAvailableDepartments(response.data.departments || []);
      // Reset designation if it's no longer available after department change
      if (bulkDesignation && !response.data.designations?.find(d => d.id === bulkDesignation)) {
        setBulkDesignation('');
      }
      // Show info about payslips found
      if (response.data.total_payslips > 0 && response.data.employees_found === 0) {
        console.warn(`Found ${response.data.total_payslips} payslips but couldn't match to employees. Employee IDs in payroll: ${response.data.payroll_employee_ids_sample?.join(', ')}`);
      }
    } catch (error) {
      console.error('Failed to fetch filters:', error);
      console.error('Error response:', error.response?.data);
      setAvailableDesignations([]);
      setAvailableDepartments([]);
    } finally {
      setLoadingFilters(false);
    }
  };

  const fetchData = async () => {
    try {
      const [empRes, companyRes, deptRes, desigRes] = await Promise.all([
        getEmployees(),
        getCompanySettings(),
        getDepartments(),
        getDesignations()
      ]);
      
      const employees = empRes.data;
      setEmployees(employees);
      setCompanyInfo(companyRes.data);
      setDepartments(deptRes.data || []);
      setDesignations(desigRes.data || []);

      let currentEmployee = null;
      if (user?.role === 'employee') {
        currentEmployee = employees.find(e => e.user_id === user?.id);
      }

      const payrollRes = await getPayroll(currentEmployee?.id);
      setPayrolls(payrollRes.data);
    } catch (error) {
      toast.error('Failed to fetch payslips');
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeDetails = (empId) => {
    return employees.find(e => e.id === empId) || null;
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : 'N/A';
  };

  const getDesignationName = (desigId) => {
    const desig = designations.find(d => d.id === desigId);
    return desig ? desig.title : 'N/A';
  };

  const handleDownloadPayslip = async (payslip) => {
    try {
      setDownloading(true);
      const response = await downloadPayslip(payslip.id);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get employee name for filename
      const employee = getEmployeeDetails(payslip.employee_id);
      const empName = employee?.name?.replace(/\s+/g, '_') || 'employee';
      link.download = `Payslip_${empName}_${payslip.month}_${payslip.year}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payslip downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      // Handle blob error response (when responseType is 'blob', error body is also a blob)
      let errorMsg = 'Failed to download payslip';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch {}
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error(errorMsg);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadForm16 = async (employeeId) => {
    if (!employeeId || !selectedFinancialYear) {
      toast.error('Please select employee and financial year');
      return;
    }
    
    setDownloadingForm16(true);
    try {
      const response = await downloadForm16(employeeId, selectedFinancialYear);
      const employee = employees.find(e => e.id === employeeId);
      const empName = (employee?.name || employee?.employee_id || 'employee').replace(/\s+/g, '_');
      const filename = `Form16_${empName}_${selectedFinancialYear}.pdf`;
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Form 16 downloaded successfully');
      setForm16DialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download Form 16');
    } finally {
      setDownloadingForm16(false);
    }
  };

  // For employees - download their own Form 16
  const handleDownloadMyForm16 = async () => {
    const currentEmployee = employees.find(e => e.user_id === user?.id);
    if (!currentEmployee) {
      toast.error('Employee record not found');
      return;
    }
    
    if (!selectedFinancialYear) {
      toast.error('Please select a financial year');
      return;
    }
    
    await handleDownloadForm16(currentEmployee.id);
  };

  // Bulk download payslips handler
  const handleBulkDownload = async () => {
    if (!bulkMonth || !bulkYear) {
      toast.error('Please select month and year');
      return;
    }
    
    setDownloadingBulk(true);
    try {
      // Add limit parameter to prevent timeout
      const response = await bulkDownloadPayslips(
        bulkMonth,
        parseInt(bulkYear),
        bulkDesignation || null,
        bulkDepartment || null
      );
      
      // Get designation name for filename
      let designationName = 'All';
      if (bulkDesignation) {
        const desig = designations.find(d => d.id === bulkDesignation);
        designationName = desig?.title || desig?.name || 'Unknown';
      }
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Payslips_${designationName.replace(/\s+/g, '_')}_${bulkMonth}_${bulkYear}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Bulk payslips downloaded successfully (limited to 50 per request)');
      setBulkDownloadDialogOpen(false);
    } catch (error) {
      console.error('Bulk download error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Please try with a smaller selection or specific department/designation filter.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to download bulk payslips');
      }
    } finally {
      setDownloadingBulk(false);
    }
  };

  // Handle downloading selected payslips
  const handleDownloadSelected = async () => {
    if (selectedPayslips.length === 0) {
      toast.error('Please select payslips to download');
      return;
    }
    
    setDownloadingSelected(true);
    try {
      const response = await downloadSelectedPayslips(selectedPayslips);
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Selected_Payslips_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${selectedPayslips.length} payslips downloaded successfully`);
      setSelectedPayslips([]);
    } catch (error) {
      console.error('Download selected error:', error);
      let errorMsg = 'Failed to download selected payslips';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch {}
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error(errorMsg);
    } finally {
      setDownloadingSelected(false);
    }
  };

  // Toggle payslip selection
  const togglePayslipSelection = (payrollId) => {
    setSelectedPayslips(prev => {
      if (prev.includes(payrollId)) {
        return prev.filter(id => id !== payrollId);
      } else {
        return [...prev, payrollId];
      }
    });
  };

  // Select all visible payslips
  const selectAllVisible = () => {
    const visibleIds = filteredPayrolls.map(p => p.id);
    setSelectedPayslips(visibleIds);
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedPayslips([]);
  };

  const filteredPayrolls = payrolls.filter(p => {
    if (filterMonth && p.month !== filterMonth) return false;
    if (filterYear && p.year.toString() !== filterYear) return false;
    
    // For admin/HR, also filter by search term
    if (user?.role !== 'employee' && searchTerm) {
      const employee = getEmployeeDetails(p.employee_id);
      const searchLower = searchTerm.toLowerCase();
      const matchesName = employee?.name?.toLowerCase().includes(searchLower);
      const matchesId = employee?.employee_id?.toLowerCase().includes(searchLower);
      if (!matchesName && !matchesId) return false;
    }
    
    return true;
  });

  // Filter employees for search dropdown
  const filteredEmployees = employees.filter(emp => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return emp.name?.toLowerCase().includes(searchLower) || 
           emp.employee_id?.toLowerCase().includes(searchLower);
  });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  // Anyone who isn't a regular employee gets search & admin view
  const isAdminOrHR = user?.role !== 'employee';

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="payslips-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">
              {isAdminOrHR ? 'Payslips & Form 16' : 'My Payslips'}
            </h1>
            <p className="text-slate-600 mt-1">
              {isAdminOrHR 
                ? 'View, search and download payslips and Form 16 for employees' 
                : 'View and download your salary payslips and Form 16'}
            </p>
          </div>
          
          {/* Form 16 Download Button for Employees */}
          {!isAdminOrHR && (
            <Dialog open={form16DialogOpen} onOpenChange={setForm16DialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <FileCheck className="mr-2" size={18} />
                  Download Form 16
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileCheck className="text-indigo-600" size={20} />
                    Download Form 16
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Financial Year *</Label>
                    <Select value={selectedFinancialYear} onValueChange={setSelectedFinancialYear}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select financial year" />
                      </SelectTrigger>
                      <SelectContent>
                        {financialYears.map(fy => (
                          <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-slate-500">
                    Form 16 is your TDS certificate for the selected financial year.
                  </p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setForm16DialogOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleDownloadMyForm16} 
                    disabled={!selectedFinancialYear || downloadingForm16}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {downloadingForm16 ? 'Generating...' : 'Download PDF'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Admin/HR: Search and Download Section */}
        {isAdminOrHR && (
          <Card className="border-2 border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="text-indigo-600" size={20} />
                Search & Download Forms
              </CardTitle>
              <CardDescription>
                Search employees by name or ID to download their payslips and Form 16
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search Input */}
                <div className="md:col-span-2">
                  <Label className="mb-2 block">Search Employee</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      placeholder="Search by name or employee ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                {/* Employee Selection for Form 16 */}
                <div>
                  <Label className="mb-2 block">Select Employee</Label>
                  <Select value={selectedEmployeeForForm16} onValueChange={setSelectedEmployeeForForm16}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredEmployees.filter(e => e.employment_status !== 'Exit').map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name || emp.employee_id} ({emp.employee_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Financial Year for Form 16 */}
                <div>
                  <Label className="mb-2 block">Financial Year</Label>
                  <Select value={selectedFinancialYear} onValueChange={setSelectedFinancialYear}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select FY" />
                    </SelectTrigger>
                    <SelectContent>
                      {financialYears.map(fy => (
                        <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Download Button */}
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => handleDownloadForm16(selectedEmployeeForForm16)}
                  disabled={!selectedEmployeeForForm16 || !selectedFinancialYear || downloadingForm16}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <FileCheck className="mr-2" size={18} />
                  {downloadingForm16 ? 'Generating...' : 'Download Form 16'}
                </Button>
                
                {/* Bulk Download Button */}
                <Dialog open={bulkDownloadDialogOpen} onOpenChange={setBulkDownloadDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-green-600 hover:bg-green-700">
                      <Archive className="mr-2" size={18} />
                      Bulk Download Payslips
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Archive className="text-green-600" size={20} />
                        Bulk Download Payslips
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <p className="text-sm text-slate-600">
                        Download payslips for all employees or filter by designation/department. 
                        Files will be downloaded as a ZIP archive.
                      </p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Month *</Label>
                          <Select value={bulkMonth} onValueChange={setBulkMonth}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select month" />
                            </SelectTrigger>
                            <SelectContent>
                              {months.map(month => (
                                <SelectItem key={month} value={month}>{month}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Year *</Label>
                          <Select value={bulkYear} onValueChange={setBulkYear}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select year" />
                            </SelectTrigger>
                            <SelectContent>
                              {years.map(year => (
                                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Show message if no month/year selected */}
                      {(!bulkMonth || !bulkYear) && (
                        <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                          Please select month and year first to see available filters
                        </p>
                      )}
                      
                      {/* Loading indicator */}
                      {loadingFilters && (
                        <div className="flex items-center gap-2 text-slate-500">
                          <Loader2 className="animate-spin" size={16} />
                          <span className="text-sm">Loading available filters...</span>
                        </div>
                      )}
                      
                      {/* Department filter - show first so designation can be filtered by it */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Building2 size={14} />
                          Filter by Department (Optional)
                        </Label>
                        <Select 
                          value={bulkDepartment || "all"} 
                          onValueChange={(v) => {
                            setBulkDepartment(v === "all" ? "" : v);
                            setBulkDesignation(''); // Reset designation when department changes
                          }}
                          disabled={!bulkMonth || !bulkYear || loadingFilters}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Departments" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Departments</SelectItem>
                            {availableDepartments.map(dept => (
                              <SelectItem key={dept.id} value={dept.id}>
                                {dept.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {bulkMonth && bulkYear && availableDepartments.length === 0 && !loadingFilters && (
                          <p className="text-xs text-slate-500">No departments with payslips for {bulkMonth} {bulkYear}</p>
                        )}
                      </div>
                      
                      {/* Designation filter - filtered by selected department */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Briefcase size={14} />
                          Filter by Designation (Optional)
                        </Label>
                        <Select 
                          value={bulkDesignation || "all"} 
                          onValueChange={(v) => setBulkDesignation(v === "all" ? "" : v)}
                          disabled={!bulkMonth || !bulkYear || loadingFilters}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Designations" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Designations</SelectItem>
                            {availableDesignations.map(desig => (
                              <SelectItem key={desig.id} value={desig.id}>
                                {desig.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {bulkMonth && bulkYear && availableDesignations.length === 0 && !loadingFilters && (
                          <p className="text-xs text-slate-500">No designations with payslips for selected criteria</p>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setBulkDownloadDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleBulkDownload} 
                        disabled={!bulkMonth || !bulkYear || downloadingBulk || loadingFilters}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Download className="mr-2" size={16} />
                        {downloadingBulk ? 'Generating ZIP...' : 'Download ZIP'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border border-slate-100 shadow-sm bg-white">
          <CardContent className="p-6 bg-white">
            <div className="flex flex-wrap gap-4">
              {isAdminOrHR && (
                <div className="flex-1 min-w-[200px]">
                  <Input
                    placeholder="Filter by employee name or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-10"
                  />
                </div>
              )}
              <div className="flex-1 min-w-[200px]">
                <Select value={filterMonth || "all"} onValueChange={(v) => setFilterMonth(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-month">
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {months.map(month => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Select value={filterYear || "all"} onValueChange={(v) => setFilterYear(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-year">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {years.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selection Actions Bar */}
        {filteredPayrolls.length > 0 && (
          <Card className={`border transition-all ${selectedPayslips.length > 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}>
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={filteredPayrolls.length > 0 && selectedPayslips.length === filteredPayrolls.length}
                  onCheckedChange={(checked) => checked ? selectAllVisible() : clearSelection()}
                  data-testid="payslip-select-all"
                />
                <span className="text-sm font-medium text-slate-700">
                  {selectedPayslips.length > 0
                    ? <span className="text-indigo-700">{selectedPayslips.length} of {filteredPayrolls.length} selected</span>
                    : `Select payslips (${filteredPayrolls.length} shown)`}
                </span>
              </div>
              {selectedPayslips.length > 0 && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={clearSelection} data-testid="payslip-clear-selection">
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                  <Button
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleDownloadSelected}
                    disabled={downloadingSelected}
                    data-testid="payslip-download-selected"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {downloadingSelected ? 'Downloading...' : `Download ${selectedPayslips.length} Payslips`}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payslips Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPayrolls.length === 0 ? (
            <Card className="col-span-3 border border-slate-100 shadow-sm bg-white">
              <CardContent className="p-12 text-center bg-white">
                <Receipt size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No payslips found</p>
                {searchTerm && (
                  <p className="text-sm text-slate-400 mt-2">
                    Try adjusting your search criteria
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredPayrolls.map((payslip, index) => {
              const employee = getEmployeeDetails(payslip.employee_id);
              return (
                <Card key={payslip.id} className={`border shadow-sm hover:shadow-lg transition-all bg-white ${selectedPayslips.includes(payslip.id) ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-slate-100'}`} data-testid={`payslip-card-${index}`}>
                  <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedPayslips.includes(payslip.id)}
                          onCheckedChange={() => togglePayslipSelection(payslip.id)}
                          data-testid={`payslip-checkbox-${index}`}
                        />
                        <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
                          <Receipt size={24} />
                        </div>
                        <div>
                          <p className="text-base font-semibold" data-testid={`payslip-period-${index}`}>{payslip.month} {payslip.year}</p>
                          {employee && (
                            <p className="text-sm font-normal text-slate-600">{employee.employee_id}</p>
                          )}
                        </div>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4 bg-white">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Gross Salary</span>
                        <span className="font-medium mono text-slate-900">₹{payslip.gross_salary?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Deductions</span>
                        <span className="font-medium mono text-rose-600">-₹{payslip.total_deductions?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="pt-3 border-t-2 border-indigo-100">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-slate-900">Net Salary</span>
                          <span className="text-2xl font-bold mono text-indigo-600" data-testid={`net-salary-${index}`}>₹{payslip.net_salary?.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="flex-1 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                            onClick={() => setSelectedPayslip(payslip)}
                            data-testid={`view-payslip-btn-${index}`}
                          >
                            View Details
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white" data-testid="payslip-detail-dialog">
                          <DialogHeader>
                            <DialogTitle className="sr-only">Salary Slip Details</DialogTitle>
                          </DialogHeader>
                          {selectedPayslip && (
                            <div className="space-y-6">
                              {/* Company Header */}
                              <div className="text-center border-b-2 border-indigo-600 pb-4">
                                {companyInfo?.logo_url && (
                                  <div className="flex justify-center mb-3">
                                    <img 
                                      src={companyInfo.logo_url} 
                                      alt="Company Logo" 
                                      className="h-16 w-16 object-contain"
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  </div>
                                )}
                                <h2 className="text-3xl font-bold text-indigo-600">
                                  {companyInfo?.company_legal_name || 'T&N Business Services Pvt Ltd'}
                                </h2>
                                <p className="text-sm text-slate-600 mt-1">
                                  {companyInfo ? `${companyInfo.address}, ${companyInfo.city}, ${companyInfo.state} - ${companyInfo.pincode}` : '3rd Floor, E-517, Sector 7 Dwarka, New Delhi - 110077'}
                                </p>
                                <p className="text-xs text-slate-500">
                                  GSTIN: {companyInfo?.gstin || '07AAHCT4353M1ZX'} | Website: {companyInfo?.website || 'www.tnservices.in'} | Email: {companyInfo?.email || 'help@tnservices.in'}
                                </p>
                              </div>

                              <div className="text-center bg-indigo-50 py-2 rounded-lg">
                                <p className="font-bold text-lg text-indigo-900">SALARY SLIP FOR THE MONTH OF {selectedPayslip.month.toUpperCase()} {selectedPayslip.year}</p>
                              </div>


                              {/* Employee Details */}
                              <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                                  <h3 className="font-bold text-slate-900 flex items-center gap-2 border-b pb-2">
                                    <UserIcon size={18} className="text-indigo-600" />
                                    Employee Details
                                  </h3>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Employee ID:</span>
                                      <span className="font-semibold mono">{employee?.employee_id || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Name:</span>
                                      <span className="font-semibold">{employee?.name || user?.full_name || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Designation:</span>
                                      <span className="font-semibold">{getDesignationName(employee?.designation_id)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Department:</span>
                                      <span className="font-semibold">{getDepartmentName(employee?.department_id)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Date of Joining:</span>
                                      <span className="font-semibold">{employee?.date_of_joining ? new Date(employee.date_of_joining).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">PAN:</span>
                                      <span className="font-semibold mono">{employee?.pan_number || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">UAN:</span>
                                      <span className="font-semibold mono">{employee?.uan_number || 'N/A'}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                                  <h3 className="font-bold text-slate-900 flex items-center gap-2 border-b pb-2">
                                    <Building2 size={18} className="text-indigo-600" />
                                    Payment Details
                                  </h3>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Pay Period:</span>
                                      <span className="font-semibold">{selectedPayslip.month} {selectedPayslip.year}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Working Days:</span>
                                      <span className="font-semibold">{selectedPayslip.working_days || 26}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Paid Days:</span>
                                      <span className="font-semibold">{selectedPayslip.paid_days || 26}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Bank Name:</span>
                                      <span className="font-semibold">{employee?.bank_name || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Account No:</span>
                                      <span className="font-semibold mono">{employee?.bank_account_number ? `XXXX${employee.bank_account_number.slice(-4)}` : 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Payment Mode:</span>
                                      <span className="font-semibold">Bank Transfer</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Earnings and Deductions */}
                              <div className="grid grid-cols-2 gap-6">
                                {/* Earnings */}
                                <div className="border border-slate-200 rounded-lg">
                                  <div className="bg-emerald-50 border-b border-emerald-200 p-3">
                                    <h3 className="font-bold text-emerald-900">EARNINGS</h3>
                                  </div>
                                  <div className="p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-slate-700">Basic Salary</span>
                                      <span className="font-medium mono">₹{selectedPayslip.basic_salary?.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-slate-700">House Rent Allowance (HRA)</span>
                                      <span className="font-medium mono">₹{selectedPayslip.hra?.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-slate-700">Special Allowance</span>
                                      <span className="font-medium mono">₹{selectedPayslip.special_allowance?.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-slate-700">Incentive</span>
                                      <span className="font-medium mono">₹{(selectedPayslip.incentive || selectedPayslip.transport_allowance || 0)?.toLocaleString('en-IN')}</span>
                                    </div>
                                    {selectedPayslip.other_allowances > 0 && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-slate-700">Other Allowances</span>
                                        <span className="font-medium mono">₹{selectedPayslip.other_allowances?.toLocaleString('en-IN')}</span>
                                      </div>
                                    )}
                                    {selectedPayslip.arrears > 0 && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-slate-700">
                                          Arrear{selectedPayslip.arrear_paid_days > 0 ? ` (${selectedPayslip.arrear_paid_days} days)` : ''}
                                        </span>
                                        <span className="font-medium mono">₹{selectedPayslip.arrears?.toLocaleString('en-IN')}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between text-sm pt-2 border-t-2 border-emerald-200 font-bold">
                                      <span className="text-emerald-900">GROSS EARNINGS</span>
                                      <span className="mono text-emerald-700">₹{selectedPayslip.gross_salary?.toLocaleString('en-IN')}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Deductions */}
                                <div className="border border-slate-200 rounded-lg">
                                  <div className="bg-rose-50 border-b border-rose-200 p-3">
                                    <h3 className="font-bold text-rose-900">DEDUCTIONS</h3>
                                  </div>
                                  <div className="p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-slate-700">Provident Fund (PF)</span>
                                      <span className="font-medium mono">₹{selectedPayslip.pf_deduction?.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-slate-700">Professional Tax (PT)</span>
                                      <span className="font-medium mono">₹{selectedPayslip.professional_tax?.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-slate-700">Tax Deducted at Source (TDS)</span>
                                      <span className="font-medium mono">₹{selectedPayslip.tds?.toLocaleString('en-IN')}</span>
                                    </div>
                                    {selectedPayslip.other_deductions > 0 && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-slate-700">Other Deductions</span>
                                        <span className="font-medium mono">₹{selectedPayslip.other_deductions?.toLocaleString('en-IN')}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between text-sm pt-2 border-t-2 border-rose-200 font-bold">
                                      <span className="text-rose-900">TOTAL DEDUCTIONS</span>
                                      <span className="mono text-rose-700">₹{selectedPayslip.total_deductions?.toLocaleString('en-IN')}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Net Salary */}
                              <div className="p-6 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg text-white">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="text-sm opacity-90">NET SALARY (Take Home)</p>
                                    <p className="text-xs opacity-75 mt-1">Gross Earnings - Total Deductions</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-4xl font-bold mono">₹{selectedPayslip.net_salary?.toLocaleString('en-IN')}</p>
                                    <p className="text-xs opacity-75 mt-1">In words: Rupees {selectedPayslip.net_salary?.toLocaleString('en-IN')} Only</p>
                                  </div>
                                </div>
                              </div>

                              {/* Footer Note */}
                              <div className="text-xs text-slate-500 border-t pt-4 space-y-2">
                                <p><strong>Note:</strong> This is a computer-generated payslip and does not require a signature.</p>
                                <p>For any queries regarding this payslip, please contact HR department at {companyInfo?.email || 'hr@tnbusiness.com'}</p>
                              </div>

                              <Button
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-6 text-lg font-semibold disabled:opacity-50"
                                onClick={() => handleDownloadPayslip(selectedPayslip)}
                                disabled={downloading}
                                data-testid="download-payslip-btn"
                              >
                                <Download className="mr-3" size={20} />
                                {downloading ? 'Downloading...' : 'Download Payslip (PDF)'}
                              </Button>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>

                      <Button
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                        onClick={() => handleDownloadPayslip(payslip)}
                        disabled={downloading}
                        data-testid={`download-btn-${index}`}
                      >
                        <Download size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Payslips;
